<script lang="ts" setup>
  import TimePicker from '@/elements/TimePicker.vue'
  import { BreakStartIcon, BreakStopIcon, DeleteIcon, OfficeIcon, PlusIcon, SendIcon, WorkStopIcon } from '@/config/icons'

  import Dialog from 'primevue/dialog'
  import FloatLabel from 'primevue/floatlabel'
  import Message from 'primevue/message'
  import { useToast } from 'primevue/usetoast'

  import { array, object, string } from 'yup'
  import { DateTime } from 'luxon'
  import { useFieldArray, useForm } from 'vee-validate'
  import { computed, toRefs, type Component } from 'vue'
  import { useTitrop } from '@/composables/useTitrop.ts'
  import type { WorktimeTrackingDayVersionEntry } from '@/api'
  import { useGeneratedWorktimeTrackingDayStore, useGeneratedWorktimeTrackingDayVersionStore } from '@/stores'
  import ButtonDefault from '@/elements/ButtonDefault.vue'

  const toastService = useToast()

  export interface AddDayFormProps {
    date: string
    employee: string
  }

  type EntryAction = 'WORK_BEGIN' | 'BREAK_BEGIN' | 'BREAK_END' | 'WORK_END'

  interface FieldObject {
    action: EntryAction
    timestamp: string | undefined
  }

  interface ActionConfig {
    label: string
    icon: Component
  }

  const actionConfig: Record<EntryAction, ActionConfig> = {
    WORK_BEGIN: { label: 'Arbeitsbeginn', icon: OfficeIcon },
    BREAK_BEGIN: { label: 'Pausenbeginn', icon: BreakStartIcon },
    BREAK_END: { label: 'Pausenende', icon: BreakStopIcon },
    WORK_END: { label: 'Arbeitsende', icon: WorkStopIcon }
  }

  const props = defineProps<AddDayFormProps>()
  const { date, employee } = toRefs(props)

  const isVisible = defineModel<boolean>('isVisible', { default: false })
  const isToday = computed(() => DateTime.now().startOf('day').toISO({ suppressMilliseconds: true }) === date.value)

  const emit = defineEmits(['entriesPost'])

  const titrop = useTitrop()
  const wttVersionStore = useGeneratedWorktimeTrackingDayVersionStore()
  const wttDayStore = useGeneratedWorktimeTrackingDayStore()

  const title = computed(() => 'Tag hinzufügen: ' + DateTime.fromISO(date.value).toFormat('dd.LL.yyyy'))

  const timestampSchema = object().shape({
    action: string().oneOf(Object.keys(actionConfig)).required(),
    timestamp: string()
      .test('isRequired', 'Dieses Feld ist erforderlich', (value, context) => {
        return Boolean(value) || isTimestampOptional(context.parent.action)
      })
      .test('isNotLaterAsNow', 'Buchungen in der Zukunft sind nicht erlaubt', (value) => {
        if (!value || !isToday.value) return true
        return (
          value <=
          DateTime.now().toISO({
            includeOffset: false,
            suppressMilliseconds: true
          })
        )
      })
  })

  const mainSchema = object().shape({
    timestamps: array()
      .of(timestampSchema)
      .required()
      .test('isStructureValid', 'Ungültige Reihenfolge oder Zeitangabe', () => checkStructure(formEntries()))
  })

  const { handleSubmit, errors, submitCount } = useForm({
    validationSchema: mainSchema,
    initialValues: {
      timestamps: [
        {
          action: 'WORK_BEGIN',
          timestamp: undefined
        },
        {
          action: 'WORK_END',
          timestamp: undefined
        }
      ]
    }
  })

  const timestampArray = useFieldArray<FieldObject>('timestamps')
  const timestampArrayFields = timestampArray.fields

  const isTimestampOptional = (action: EntryAction | undefined) => {
    return isToday.value && (action === 'BREAK_END' || action === 'WORK_END')
  }

  const canAddPause = computed(() => timestampArrayFields.value.slice(0, -1).every((entry) => Boolean(entry.value.timestamp)))

  const addPause = () => {
    if (!canAddPause.value) return
    const workEndIndex = timestampArrayFields.value.length - 1
    timestampArray.insert(workEndIndex, { action: 'BREAK_BEGIN', timestamp: undefined })
    timestampArray.insert(workEndIndex + 1, { action: 'BREAK_END', timestamp: undefined })
  }

  const canRemovePause = (index: number) => {
    const action = timestampArrayFields.value[index]?.value.action
    if (action === 'BREAK_END') return true
    return action === 'BREAK_BEGIN' && timestampArrayFields.value[index + 1]?.value.action !== 'BREAK_END'
  }

  const removePause = (index: number) => {
    if (!canRemovePause(index)) return
    if (timestampArrayFields.value[index].value.action === 'BREAK_END' && timestampArrayFields.value[index - 1]?.value.action === 'BREAK_BEGIN') {
      timestampArray.remove(index)
      timestampArray.remove(index - 1)
      return
    }
    timestampArray.remove(index)
  }

  const formatDuration = (milliseconds: number) => {
    const totalMinutes = Math.max(0, Math.floor(milliseconds / 60_000))
    return `${Math.floor(totalMinutes / 60)}:${String(totalMinutes % 60).padStart(2, '0')}`
  }

  const timeSummary = computed(() => {
    let totalWork = 0
    let totalBreak = 0
    let workStart: number | undefined
    let breakStart: number | undefined

    timestampArrayFields.value.forEach(({ value: entry }) => {
      if (!entry.timestamp) return
      const timestamp = DateTime.fromISO(entry.timestamp).toMillis()
      if (!Number.isFinite(timestamp)) return

      switch (entry.action) {
        case 'WORK_BEGIN':
          workStart = timestamp
          break
        case 'BREAK_BEGIN':
          breakStart = timestamp
          break
        case 'BREAK_END':
          if (breakStart !== undefined) totalBreak += Math.max(0, timestamp - breakStart)
          breakStart = undefined
          break
        case 'WORK_END':
          if (workStart !== undefined) totalWork += Math.max(0, timestamp - workStart)
          workStart = undefined
          break
      }
    })

    if (isToday.value) {
      const now = DateTime.now().toMillis()
      if (workStart !== undefined) totalWork += Math.max(0, now - workStart)
      if (breakStart !== undefined) totalBreak += Math.max(0, now - breakStart)
    }

    return {
      work: formatDuration(totalWork - totalBreak),
      break: formatDuration(totalBreak)
    }
  })

  const onSubmit = handleSubmit(async () => {
    const entriesArray = formEntries()
    const dayRes = await wttDayStore.post({
      body: {
        date: date.value,
        employee: employee.value
      }
    })
    await wttVersionStore.post({
      body: {
        creator: titrop.value.user.currentIri,
        entries: entriesArray,
        day: dayRes['@id'],
        decision: titrop.value.user.roles.management || titrop.value.user.roles.worktime_selfedit ? 'APPROVED' : undefined
      }
    })
    if (titrop.value.user.roles.management || titrop.value.user.roles.worktime_selfedit)
      toastService.add({
        severity: 'success',
        life: 2500,
        summary: 'Gespeichert'
      })
    else toastService.add({ severity: 'success', life: 2500, summary: 'Die Anfrage wurde gesendet' })
    emit('entriesPost')
    isVisible.value = false
  })

  const formEntries = (): Partial<WorktimeTrackingDayVersionEntry>[] => {
    return timestampArrayFields.value.flatMap((field) => {
      if (!field.value.timestamp) return []
      return [
        {
          action: field.value.action,
          timestamp: field.value.timestamp,
          draft: false
        }
      ]
    })
  }

  function checkStructure(actions: Partial<WorktimeTrackingDayVersionEntry>[]) {
    if (!actions.length) return false

    let state: 'idle' | 'working' | 'break' | 'complete' = 'idle'
    let previousTimestamp: string | undefined

    for (const action of actions) {
      if (!action.timestamp || (previousTimestamp && action.timestamp <= previousTimestamp)) return false

      switch (action.action) {
        case 'WORK_BEGIN':
          if (state !== 'idle') return false
          state = 'working'
          break
        case 'BREAK_BEGIN':
          if (state !== 'working') return false
          state = 'break'
          break
        case 'BREAK_END':
          if (state !== 'break') return false
          state = 'working'
          break
        case 'WORK_END':
          if (state !== 'working') return false
          state = 'complete'
          break
        default:
          return false
      }

      previousTimestamp = action.timestamp
    }

    return isToday.value ? state !== 'idle' : state === 'complete'
  }
</script>

<template>
  <Dialog
    v-model:visible="isVisible"
    :header="title"
    :style="{ width: '36rem' }"
    @keydown.capture.ctrl.enter.prevent="onSubmit"
    modal>
    <form @submit.prevent="onSubmit">
      <ol
        class="m-0 flex list-none flex-col gap-3 p-0"
        aria-label="Zeitverlauf">
        <template
          v-for="(entry, index) in timestampArrayFields"
          :key="entry.key">
          <li
            v-if="entry.value.action === 'WORK_END'"
            class="grid min-h-10 grid-cols-[1.25rem_minmax(0,1fr)_2rem] gap-3">
            <span
              class="relative after:absolute after:top-0 after:bottom-[-1.875rem] after:left-[0.5625rem] after:border-l-2 after:border-dashed after:border-[var(--p-content-border-color)] after:content-['']"
              aria-hidden="true" />
            <ButtonDefault
              :disabled="!canAddPause"
              :icon="PlusIcon"
              label="Pause hinzufügen"
              class="!pl-0 justify-self-start self-start"
              severity="secondary"
              size="small"
              type="button"
              variant="text"
              @click="addPause" />
            <span aria-hidden="true" />
          </li>

          <li
            :class="[
              'grid min-h-14 grid-cols-[1.25rem_minmax(0,1fr)_2rem] gap-3',
              index === timestampArrayFields.length - 2
                ? '[&_.worktime-marker]:after:w-0 [&_.worktime-marker]:after:border-l-2 [&_.worktime-marker]:after:border-dashed [&_.worktime-marker]:after:border-[var(--p-content-border-color)] [&_.worktime-marker]:after:bg-transparent'
                : '',
              entry.value.action === 'WORK_END' && index === timestampArrayFields.length - 1 ? '[&_.worktime-marker]:after:hidden' : ''
            ]">
            <span
              class="worktime-marker relative self-stretch after:absolute after:top-6 after:bottom-[-1.875rem] after:left-[0.5625rem] after:w-0.5 after:bg-[var(--p-content-border-color)] after:content-['']"
              aria-hidden="true">
              <span class="absolute top-[1.125rem] left-[0.3125rem] z-[1] size-2.5 rounded-full border-2 border-[var(--p-content-background)] bg-[var(--p-primary-color)] shadow-[0_0_0_1px_var(--p-primary-color)]" />
            </span>

            <div class="grid grid-cols-[minmax(8.5rem,0.8fr)_minmax(11rem,1.2fr)] items-center gap-3 max-sm:grid-cols-[minmax(0,1fr)] max-sm:gap-2">
              <div class="hidden min-w-0 items-center gap-2 md:flex">
                <component
                  :is="actionConfig[entry.value.action].icon"
                  class="size-4.5 shrink-0 text-[var(--p-text-muted-color)]"
                  aria-hidden="true" />
                <span class="font-semibold">{{ actionConfig[entry.value.action].label }}</span>
              </div>

              <div class="min-w-0">
                <div class="flex min-w-0 items-center gap-1">
                  <FloatLabel
                    class="min-w-0 grow"
                    variant="on">
                    <label class="hidden md:block">Zeit:</label>
                    <label class="flex min-w-0 items-center gap-2 md:hidden">
                      <component
                        :is="actionConfig[entry.value.action].icon"
                        class="size-4.5 shrink-0 text-[var(--p-text-muted-color)]"
                        aria-hidden="true" />
                      <span class="font-semibold">{{ actionConfig[entry.value.action].label }}</span>
                    </label>
                    <TimePicker
                      v-model="entry.value.timestamp"
                      :base-date="date"
                      :class="{ invalid: errors[`timestamps[${index}].timestamp`] && submitCount }" />
                  </FloatLabel>
                </div>
                <small
                  v-if="errors[`timestamps[${index}].timestamp`] && submitCount"
                  class="mt-1 block text-[var(--p-form-field-invalid-border-color)]"
                  role="alert">
                  {{ errors[`timestamps[${index}].timestamp`] }}
                </small>
              </div>
            </div>

            <ButtonDefault
              v-if="canRemovePause(index)"
              v-tooltip="{ value: 'Pause entfernen', modifiers: { top: true } }"
              :icon="DeleteIcon"
              aria-label="Pause entfernen"
              class="size-8 self-center"
              outlined
              severity="danger"
              size="extra-small"
              type="button"
              @click="removePause(index)" />
            <span
              v-else
              aria-hidden="true" />
          </li>
        </template>
      </ol>

      <div
        v-if="submitCount && errors['timestamps']"
        class="mt-4">
        <Message class="w-full">
          {{ errors['timestamps'] }}
        </Message>
      </div>
    </form>

    <template #footer>
      <div class="grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-4 max-sm:grid-cols-1">
        <div
          class="flex flex-wrap justify-center gap-x-4 gap-y-1 text-sm text-[var(--p-text-muted-color)] [&_strong]:text-[var(--p-text-color)]"
          aria-live="polite">
          <span>
            Arbeitszeit:
            <strong>{{ timeSummary.work }}</strong>
          </span>
          <span>
            Pause:
            <strong>{{ timeSummary.break }}</strong>
          </span>
        </div>
        <ButtonDefault
          :icon="SendIcon"
          label="Absenden"
          size="small"
          @click="onSubmit" />
      </div>
    </template>
  </Dialog>
</template>
